/**
 * Hook for PWA installation functionality
 * @module hooks/usePWAInstall
 * @version 6.11.43.00
 *
 * v6.11.43.00 - Dos arreglos:
 *   1. `beforeinstallprompt` se captura a nivel de módulo, no dentro del efecto.
 *      El navegador dispara ese evento **una sola vez y temprano** (apenas
 *      carga la página); si el componente que usa el hook se monta después
 *      —que es siempre: `BrowserBlockScreen` aparece recién cuando el gate de
 *      `App.tsx` resuelve, y `PWAInstallPrompt` tiene 2,5 s de delay— el
 *      listener llegaba tarde y el evento ya se había perdido. Resultado: en
 *      Chrome el botón de instalar aparecía o no según el timing.
 *   2. Nuevo `nativeInstallAvailable`: haber capturado el evento NO alcanza
 *      para mostrar el botón (ver el comentario en la interfaz).
 */

import { useState, useEffect, useCallback } from 'react';
import { getDeviceInfo } from '../utils/deviceUtils';

/**
 * BeforeInstallPromptEvent interface
 * This event is fired when the browser determines the PWA can be installed
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

// Storage key for user dismissal preference
const INSTALL_DISMISSED_KEY = 'pwa-install-dismissed';
const INSTALL_DISMISSED_AT_KEY = 'pwa-install-dismissed-at';

// Re-show prompt after 7 days if user dismissed
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Captura global del evento de instalación
//
// Vive fuera de React porque el evento llega antes que cualquier componente.
// El módulo se importa en el arranque (`App.tsx` importa `BrowserBlockScreen`
// de forma estática), así que el listener queda puesto a tiempo.
// ---------------------------------------------------------------------------

let capturedPrompt: BeforeInstallPromptEvent | null = null;
let appWasInstalled = false;

type InstallStateListener = () => void;
const listeners = new Set<InstallStateListener>();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    // Frenamos el mini-infobar del navegador: la invitación la damos nosotros.
    e.preventDefault();
    capturedPrompt = e as BeforeInstallPromptEvent;
    console.log('[PWA] Install prompt captured and ready');
    notify();
  });

  window.addEventListener('appinstalled', () => {
    capturedPrompt = null;
    appWasInstalled = true;

    // Clear any dismissal state
    localStorage.removeItem(INSTALL_DISMISSED_KEY);
    localStorage.removeItem(INSTALL_DISMISSED_AT_KEY);

    console.log('[PWA] App installed successfully');
    notify();
  });
}

/** ¿Hay un descarte vigente (dentro del cooldown)? Limpia el vencido. */
const isDismissalActive = (): boolean => {
  const dismissedAt = localStorage.getItem(INSTALL_DISMISSED_AT_KEY);
  if (!dismissedAt) return false;

  const elapsed = Date.now() - parseInt(dismissedAt, 10);
  if (elapsed < DISMISS_COOLDOWN_MS) return true;

  // Cooldown expired, clear dismissal
  localStorage.removeItem(INSTALL_DISMISSED_KEY);
  localStorage.removeItem(INSTALL_DISMISSED_AT_KEY);
  return false;
};

export interface UsePWAInstallReturn {
  /** Whether the PWA can be installed (prompt is available or manual install applies) */
  isInstallable: boolean;
  /** Whether the PWA is already installed (running in standalone mode) */
  isInstalled: boolean;
  /** Whether the device is iOS (requires manual install instructions) */
  isIOS: boolean;
  /** Whether the device is Samsung Internet (may require manual install instructions) */
  isSamsungBrowser: boolean;
  /** Whether a native install prompt is available (beforeinstallprompt was captured) */
  hasNativePrompt: boolean;
  /**
   * Whether the one-tap install button can actually be shown.
   *
   * No alcanza con haber capturado `beforeinstallprompt`: Samsung Internet lo
   * dispara pero después `prompt()` no abre nada, así que el botón queda
   * muerto. Ahí (y en iOS, que directamente no tiene la API) el único camino
   * son las instrucciones manuales de `utils/installSteps`.
   */
  nativeInstallAvailable: boolean;
  /** Whether the device is mobile */
  isMobile: boolean;
  /** Whether the install prompt should be shown */
  canShowPrompt: boolean;
  /** Whether user has previously dismissed the prompt */
  userDismissed: boolean;
  /** Trigger the native install prompt (Android/Desktop) */
  promptInstall: () => Promise<boolean>;
  /** Record user dismissal of the prompt */
  dismissPrompt: (permanent?: boolean) => void;
  /** Reset dismissal state (for testing or re-prompting) */
  resetDismissal: () => void;
}

export const usePWAInstall = (): UsePWAInstallReturn => {
  const deviceInfo = getDeviceInfo();

  // Estado inicial leído del módulo: si el evento ya llegó, el hook nace
  // sabiéndolo (era justo el caso que se perdía).
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(
    () => capturedPrompt
  );
  const [isInstalled, setIsInstalled] = useState(
    () => deviceInfo.isStandalone || appWasInstalled
  );
  const [userDismissed, setUserDismissed] = useState(() => isDismissalActive());

  useEffect(() => {
    // Re-sincronizar con lo que haya pasado entre el render inicial y el efecto,
    // y quedar suscrito a los eventos globales de arriba.
    const sync = () => {
      setDeferredPrompt(capturedPrompt);
      if (appWasInstalled) {
        setIsInstalled(true);
        setUserDismissed(false);
      }
    };

    listeners.add(sync);
    sync();

    return () => {
      listeners.delete(sync);
    };
  }, []);

  /**
   * Trigger the native install prompt (Android/Desktop)
   * Returns true if user accepted, false if dismissed
   */
  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) {
      console.warn('[PWA] Install prompt not available');
      return false;
    }

    try {
      // Show native install prompt
      await deferredPrompt.prompt();

      // Wait for user response
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('[PWA] User accepted install prompt');
        capturedPrompt = null;
        setDeferredPrompt(null);
        return true;
      }

      console.log('[PWA] User dismissed install prompt');
      return false;
    } catch (error) {
      // Un prompt() que falla (o que el navegador ignora) no puede dejar al
      // usuario sin camino: quien llama muestra los pasos manuales.
      console.error('[PWA] Error showing install prompt:', error);
      return false;
    }
  }, [deferredPrompt]);

  /**
   * Record user dismissal of the prompt
   * @param permanent - If true, dismiss forever; if false, dismiss with cooldown
   */
  const dismissPrompt = useCallback((permanent: boolean = false) => {
    setUserDismissed(true);

    if (permanent) {
      localStorage.setItem(INSTALL_DISMISSED_KEY, 'permanent');
    } else {
      localStorage.setItem(INSTALL_DISMISSED_AT_KEY, Date.now().toString());
    }

    console.log(`[PWA] User dismissed prompt (permanent: ${permanent})`);
  }, []);

  /**
   * Reset dismissal state (for testing or re-prompting)
   */
  const resetDismissal = useCallback(() => {
    setUserDismissed(false);
    localStorage.removeItem(INSTALL_DISMISSED_KEY);
    localStorage.removeItem(INSTALL_DISMISSED_AT_KEY);
    console.log('[PWA] Dismissal state reset');
  }, []);

  // Instalable = hay evento nativo, o es un celular donde la instalación se
  // hace a mano (iOS, Samsung Internet, Firefox…). Desde v6.11.43.00 hay pasos
  // escritos para todos, así que ya no se limita a iOS/Samsung.
  const isInstallable = !isInstalled && (deferredPrompt !== null || deviceInfo.isMobile);

  // Botón de instalación en un toque: solo donde realmente instala.
  const nativeInstallAvailable =
    deferredPrompt !== null && !deviceInfo.isSamsungBrowser && !deviceInfo.isIOS;

  // Determine if we should show the prompt
  const canShowPrompt =
    deviceInfo.isMobile &&
    isInstallable &&
    !isInstalled &&
    !userDismissed;

  return {
    isInstallable,
    isInstalled,
    isIOS: deviceInfo.isIOS,
    isSamsungBrowser: deviceInfo.isSamsungBrowser,
    hasNativePrompt: deferredPrompt !== null,
    nativeInstallAvailable,
    isMobile: deviceInfo.isMobile,
    canShowPrompt,
    userDismissed,
    promptInstall,
    dismissPrompt,
    resetDismissal
  };
};
